import { useContext, useEffect, useRef, useState } from "react";
import { App, Descriptions, Empty, Modal, Spin, Tag } from "antd";
import type { DescriptionsProps } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import errorCodes from "@/apis/error";
import { fetch as fetchEquipment } from "@/apis/equipment";
import { DictContext } from "@/layout/context/DictContext";
import { Equipment } from "@/types/equipment";
import { getUserName } from "@/utils/user";
import { useEquipmentEnumsContext } from "../../context/EquipmentEnumsContext";

// 设备详情弹窗参数
interface EquipmentDetailProps {
  open: boolean; // 弹窗是否打开
  equipmentId?: string; // 当前设备 ID
  onCancel: () => void; // 关闭弹窗
}

// 为缺失的普通字段提供统一占位符
const renderValue = (value?: string | number | null) => value ?? "-";

// 将时间戳格式化为日期或完整时间
const renderDate = (value?: number | null, withTime = false) => value
  ? dayjs(value).format(withTime ? "YYYY-MM-DD HH:mm:ss" : "YYYY-MM-DD")
  : "-";

// 加载并展示指定设备的完整详情
const EquipmentDetail = ({
  open,
  equipmentId,
  onCancel,
}: EquipmentDetailProps) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const { currencys } = useContext(DictContext);
  const { statusItems } = useEquipmentEnumsContext();
  const [equipment, setEquipment] = useState<Equipment>();
  const [loading, setLoading] = useState(false);
  const controllerRef = useRef<AbortController>(); // 当前详情请求控制器

  useEffect(() => {
    // 切换设备或关闭弹窗时取消上一条详情请求
    controllerRef.current?.abort();

    if (!open || !equipmentId) {
      setEquipment(undefined);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    controllerRef.current = controller;
    setEquipment(undefined);
    setLoading(true);
    fetchEquipment(equipmentId, controller)
      .then((detail) => {
        // 仅允许最新请求更新详情，避免快速切换时数据错位
        if (controllerRef.current === controller) {
          setEquipment(detail);
        }
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          notification.error({
            message: t("equipment.equipmentFetchFailed"),
            description: errorCodes[Number(error)] ?? String(error),
          });
        }
      })
      .finally(() => {
        if (controllerRef.current === controller) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [equipmentId, notification, open, t]);

  // 将币种字典值转换为可读名称，缺少字典时保留原值
  const currencyLabel = equipment?.purchaseCurrency === null ||
    equipment?.purchaseCurrency === undefined
    ? "-"
    : currencys.find(
      ({ key }) => Number(key) === equipment.purchaseCurrency,
    )?.desc ?? String(equipment.purchaseCurrency);

  // 将设备详情整理为 Descriptions 可渲染的数据项
  const detailItems: DescriptionsProps["items"] = equipment
    ? [
      {
        key: "number",
        label: t("equipment.equipmentNumber"),
        children: renderValue(equipment.number),
      },
      {
        key: "name",
        label: t("equipment.equipmentName"),
        children: equipment.name,
      },
      {
        key: "category",
        label: t("equipment.equipmentCategory"),
        children: equipment.category?.name ?? "-",
      },
      {
        key: "status",
        label: t("equipment.equipmentStatus"),
        children: (
          <Tag>
            {statusItems.find(({ v }) => v === equipment.status)?.desc ??
              String(equipment.status)}
          </Tag>
        ),
      },
      {
        key: "brand",
        label: t("equipment.brand"),
        children: renderValue(equipment.brand),
      },
      {
        key: "model",
        label: t("equipment.model"),
        children: renderValue(equipment.model),
      },
      {
        key: "serialNo",
        label: t("equipment.serialNo"),
        children: renderValue(equipment.serialNo),
      },
      {
        key: "qrCode",
        label: t("equipment.qrCode"),
        children: renderValue(equipment.qrCode),
      },
      {
        key: "keeperOrg",
        label: t("equipment.keeperOrg"),
        children: equipment.keeperOrg?.name ?? "-",
      },
      {
        key: "ownedOrg",
        label: t("equipment.ownedOrg"),
        children: equipment.org?.name ?? "-",
      },
      {
        key: "currentUser",
        label: t("equipment.currentUser"),
        children: equipment.currentUser
          ? getUserName(equipment.currentUser)
          : "-",
      },
      {
        key: "location",
        label: t("equipment.location"),
        children: renderValue(equipment.location),
      },
      {
        key: "placeLocation",
        label: t("equipment.placeLocation"),
        children: renderValue(equipment.placeLocation),
      },
      {
        key: "maintainDays",
        label: t("equipment.maintainDays"),
        children: renderValue(equipment.maintainDays),
      },
      {
        key: "maintainThreshold",
        label: t("equipment.maintainThreshold"),
        children: renderValue(equipment.maintainThreshold),
      },
      {
        key: "lastMaintainAt",
        label: t("equipment.lastMaintainAt"),
        children: renderDate(equipment.lastMaintainAt, true),
      },
      {
        key: "lastMaintainOdometer",
        label: t("equipment.lastMaintainOdometer"),
        children: renderValue(equipment.lastMaintainOdometer),
      },
      {
        key: "currentOdometer",
        label: t("equipment.currentOdometer"),
        children: renderValue(equipment.currentOdometer),
      },
      {
        key: "purchaseAt",
        label: t("equipment.purchaseAt"),
        children: renderDate(equipment.purchaseAt),
      },
      {
        key: "purchasePrice",
        label: t("equipment.purchasePrice"),
        children: renderValue(equipment.purchasePrice),
      },
      {
        key: "purchaseCurrency",
        label: t("equipment.purchaseCurrency"),
        children: currencyLabel,
      },
      {
        key: "supplier",
        label: t("equipment.supplier"),
        children: renderValue(equipment.supplier),
      },
    ]
    : [];

  return (
    <Modal
      title={t("equipment.equipmentDetail")}
      open={open}
      width={900}
      footer={null}
      destroyOnClose
      styles={{ body: { maxHeight: "65vh", overflowY: "auto" } }}
      onCancel={onCancel}
    >
      {loading ? (
        <div style={{ padding: 48, textAlign: "center" }}>
          <Spin />
        </div>
      ) : equipment ? (
        <Descriptions
          bordered
          size="small"
          column={2}
          items={detailItems}
        />
      ) : (
        <Empty description={t("equipment.noEquipmentDetail")} />
      )}
    </Modal>
  );
};

export default EquipmentDetail;
